import React, { useState, useEffect, useMemo, useRef } from "react";
import { BottomSheet } from "../../../shared/components/BottomSheet.jsx";
import { Button } from "../../../shared/components/Button.jsx";
import { Badge } from "../../../shared/components/Badge.jsx";
import { formatAppDate } from "../../../shared/utils/dateUtils.js";
import { calculateFifoAllocations, validateManualAllocations } from "../../invoices/utils/paymentCalculations.js";
import { recordVendorPaymentTally, updatePayment } from "../../invoices/services/paymentService.js";
import { usePurchases } from "../hooks/usePurchases.js";
import { useCompanyParties } from "../../customers/hooks/useCompanyParties.js";
import { useActiveCompany } from "../../../shared/context/ActiveCompanyContext.jsx";

const PAYMENT_MODES = [
  "Cash",
  "UPI / GPay / PhonePe",
  "Bank Transfer / NEFT",
  "Cheque"
];

function normalizePurchaseBills(openBills) {
  // Map purchase bill fields to the shape expected by calculateFifoAllocations
  return openBills.map((bill) => ({
    ...bill,
    // Alias purchase fields to invoice field names for reuse of FIFO utility
    invoiceId: bill.id,
    invoiceNo: bill.vendorBillNo || bill.id,
    invoiceDate: bill.billDate || bill.createdAt || "",
    grandTotal: Number(bill.grandTotal || 0),
    paid: Number(bill.paid || 0),
    balance: Number(bill.balance !== undefined ? bill.balance : Math.max(0, Number(bill.grandTotal || 0) - Number(bill.paid || 0)))
  }));
}

export function VendorPaymentModal({
  isOpen,
  onClose,
  preselectedVendor = null,
  onPaymentSaved,
  editingPayment = null // existing PAYMENT_OUT record to modify (vendor is locked)
}) {
  const isEditing = !!editingPayment?.id;
  // Working company (header): only its vendors and purchase bills; payments are saved under it
  const { purchases: allPurchases, refetch: refetchPurchases } = usePurchases();
  const { customers, refetch: refetchCustomers, dueOf } = useCompanyParties();
  const { activeCompany: defaultCompany, activeCompanyId, ownsRecord } = useActiveCompany();
  const purchases = useMemo(() => allPurchases.filter(ownsRecord), [allPurchases, ownsRecord]);

  const [selectedVendorId, setSelectedVendorId] = useState("");
  const [vendorSearch, setVendorSearch] = useState("");
  const [isVendorDropdownOpen, setIsVendorDropdownOpen] = useState(false);

  const [amountPaid, setAmountPaid] = useState("");
  const [paymentDate, setPaymentDate] = useState(() => new Date().toISOString().split("T")[0]);
  const [paymentMode, setPaymentMode] = useState("Bank Transfer / NEFT");
  const [reference, setReference] = useState("");
  const [notes, setNotes] = useState("");

  const [allocations, setAllocations] = useState([]);
  const [isManualModified, setIsManualModified] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // Vendors: customers with partyType 'vendor' or 'both'
  const vendors = useMemo(() => {
    return (customers || []).filter(
      (c) => c.partyType === "vendor" || c.partyType === "both"
    );
  }, [customers]);

  // Old allocation per bill for the payment being edited (treated as reversed while editing)
  const editAllocByBill = useMemo(() => {
    const map = {};
    (editingPayment?.allocations || []).forEach((a) => {
      const id = a.purchaseId || a.invoiceId;
      if (id) map[id] = (map[id] || 0) + Number(a.allocatedAmount || 0);
    });
    return map;
  }, [editingPayment]);

  // Seed edit state once per opened payment (customers refetching must not wipe edits)
  const seededEditIdRef = useRef(null);
  useEffect(() => {
    if (!isOpen) {
      seededEditIdRef.current = null;
      return;
    }
    if (isEditing && seededEditIdRef.current !== editingPayment.id) {
      seededEditIdRef.current = editingPayment.id;
      setSelectedVendorId(editingPayment.partyId || editingPayment.vendorId);
      setAmountPaid(String(editingPayment.amount || ""));
      setPaymentDate(editingPayment.paymentDate || new Date().toISOString().split("T")[0]);
      setPaymentMode(editingPayment.paymentMode || "Bank Transfer / NEFT");
      setReference(editingPayment.reference || "");
      setNotes(editingPayment.notes || "");
      setErrorMessage("");
      setIsManualModified(true); // keep the original split until the user changes the amount
    }
  }, [isOpen, isEditing, editingPayment]);

  // Initialize or sync selected vendor
  useEffect(() => {
    if (isOpen && !isEditing) {
      if (preselectedVendor?.id) {
        setSelectedVendorId(preselectedVendor.id);
      } else if (!selectedVendorId && vendors.length > 0) {
        const vendorWithBalance = vendors.find((v) => dueOf(v) > 0);
        setSelectedVendorId(vendorWithBalance ? vendorWithBalance.id : vendors[0].id);
      }
      setAmountPaid("");
      setReference("");
      setNotes("");
      setErrorMessage("");
      setIsManualModified(false);
      setPaymentDate(new Date().toISOString().split("T")[0]);
    }
  }, [isOpen, preselectedVendor, vendors, isEditing]);

  const currentVendor = useMemo(() => {
    if (!selectedVendorId) return null;
    return customers?.find((c) => c.id === selectedVendorId) ||
      preselectedVendor ||
      (isEditing ? { id: selectedVendorId, name: editingPayment.partyName || editingPayment.vendorName } : null);
  }, [selectedVendorId, customers, preselectedVendor, isEditing, editingPayment]);

  // Open purchase bills for the selected vendor (balance > 0, oldest first)
  const openVendorBills = useMemo(() => {
    if (!selectedVendorId || !purchases) return [];
    // When editing, this payment's own allocations are added back so they can be re-split
    const adjusted = purchases.map((p) => {
      const oldAlloc = editAllocByBill[p.id] || 0;
      if (!oldAlloc) return p;
      const paid = Number(p.paid || 0);
      const balance = Number(p.balance !== undefined ? p.balance : Math.max(0, Number(p.grandTotal || 0) - paid));
      return { ...p, paid: Math.max(0, paid - oldAlloc), balance: balance + oldAlloc };
    });
    const normalized = normalizePurchaseBills(
      adjusted.filter((p) => {
        const matchesVendor =
          p.vendorId === selectedVendorId ||
          (currentVendor?.name &&
            p.vendorName &&
            p.vendorName.toLowerCase() === currentVendor.name.toLowerCase());
        const bal = Number(
          p.balance !== undefined
            ? p.balance
            : Math.max(0, Number(p.grandTotal || 0) - Number(p.paid || 0))
        );
        return matchesVendor && bal > 0;
      })
    );
    return normalized.sort((a, b) => {
      const aDate = new Date(a.invoiceDate || a.createdAt || 0).getTime();
      const bDate = new Date(b.invoiceDate || b.createdAt || 0).getTime();
      if (aDate !== bDate) return aDate - bDate;
      return String(a.invoiceNo || "").localeCompare(String(b.invoiceNo || ""), undefined, { numeric: true });
    });
  }, [selectedVendorId, purchases, currentVendor, editAllocByBill]);

  const totalOpenBalance = useMemo(() => {
    return openVendorBills.reduce((sum, b) => sum + Number(b.balance || 0), 0);
  }, [openVendorBills]);

  // Sync FIFO allocations whenever amountPaid or openVendorBills changes
  useEffect(() => {
    if (!isManualModified) {
      const result = calculateFifoAllocations(openVendorBills, amountPaid);
      setAllocations(result.allocations);
    }
  }, [amountPaid, openVendorBills, isManualModified]);

  // Editing: start from the payment's original split across bills
  const seededAllocIdRef = useRef(null);
  useEffect(() => {
    if (!isOpen) {
      seededAllocIdRef.current = null;
      return;
    }
    if (!isEditing || seededAllocIdRef.current === editingPayment.id || openVendorBills.length === 0) return;
    if (selectedVendorId !== (editingPayment.partyId || editingPayment.vendorId)) return; // wait for party switch
    seededAllocIdRef.current = editingPayment.id;
    const base = calculateFifoAllocations(openVendorBills, 0).allocations;
    setAllocations(base.map((a) => {
      const amt = Math.min(editAllocByBill[a.invoiceId] || 0, a.balanceDue);
      const rem = Math.round(Math.max(0, a.balanceDue - amt) * 100) / 100;
      return {
        ...a,
        allocatedAmount: amt,
        remainingBalance: rem,
        nextStatus: rem === 0 ? "Paid" : (a.alreadyPaid + amt > 0 ? "Partial" : "Unpaid")
      };
    }));
  }, [isOpen, isEditing, editingPayment, openVendorBills, editAllocByBill, selectedVendorId]);

  const paidNum = Math.max(0, Number(amountPaid) || 0);

  const { totalApplied, unallocatedAmount } = useMemo(() => {
    if (!allocations.length) {
      return { totalApplied: 0, unallocatedAmount: paidNum };
    }
    const applied = allocations.reduce((s, a) => s + Number(a.allocatedAmount || 0), 0);
    return {
      totalApplied: Math.round(applied * 100) / 100,
      unallocatedAmount: Math.round(Math.max(0, paidNum - applied) * 100) / 100
    };
  }, [allocations, paidNum]);

  const handleManualAllocationChange = (invoiceId, value) => {
    setIsManualModified(true);
    setAllocations((prev) =>
      prev.map((a) => {
        if (a.invoiceId !== invoiceId) return a;
        const newAmt = Math.max(0, Math.min(Number(value) || 0, a.balanceDue));
        const remainingBalance = Math.round(Math.max(0, a.balanceDue - newAmt) * 100) / 100;
        const nextStatus = remainingBalance === 0 ? "Paid" : newAmt > 0 ? "Partial" : "Unpaid";
        return { ...a, allocatedAmount: newAmt, remainingBalance, nextStatus };
      })
    );
    const validation = validateManualAllocations(allocations, amountPaid);
    if (!validation.isValid) {
      setErrorMessage(validation.error);
    } else {
      setErrorMessage("");
    }
  };

  const filteredVendorsForPicker = useMemo(() => {
    const term = vendorSearch.toLowerCase().trim();
    return vendors.filter((v) => {
      if (!term) return true;
      const name = (v.name || "").toLowerCase();
      const phone = (v.mobile || "").toLowerCase();
      return name.includes(term) || phone.includes(term);
    });
  }, [vendors, vendorSearch]);

  const handleSavePayment = async (e) => {
    e.preventDefault();
    setErrorMessage("");

    if (!selectedVendorId) {
      setErrorMessage("Please select a vendor.");
      return;
    }
    if (paidNum <= 0) {
      setErrorMessage("Please enter a valid payment amount.");
      return;
    }
    if (totalApplied <= 0 && unallocatedAmount <= 0) {
      setErrorMessage("Please allocate payment across at least one purchase bill.");
      return;
    }

    const billAllocations = allocations.map((a) => ({
      purchaseId: a.invoiceId,
      billNo: a.invoiceNo,
      billDate: a.invoiceDate,
      grandTotal: a.grandTotal,
      balanceDue: a.balanceDue,
      allocatedAmount: a.allocatedAmount,
      remainingBalance: a.remainingBalance,
      nextStatus: a.nextStatus
    }));

    setSubmitting(true);
    try {
      if (isEditing) {
        const updated = await updatePayment(editingPayment.id, {
          amount: paidNum,
          totalApplied,
          unallocatedAmount,
          paymentDate,
          paymentMode,
          reference,
          notes,
          allocations: billAllocations
        });
        if (refetchPurchases) await refetchPurchases();
        if (refetchCustomers) await refetchCustomers();
        if (onPaymentSaved) onPaymentSaved(updated);
        onClose();
        return;
      }

      const result = await recordVendorPaymentTally({
        vendorId: selectedVendorId,
        vendorName: currentVendor?.name || "Vendor",
        vendorPhone: currentVendor?.mobile || "",
        companyId: activeCompanyId || null,
        amount: paidNum,
        totalApplied,
        unallocatedAmount,
        paymentDate,
        paymentMode,
        reference,
        notes,
        allocations: billAllocations
      });

      if (refetchPurchases) await refetchPurchases();
      if (refetchCustomers) await refetchCustomers();
      if (onPaymentSaved) onPaymentSaved(result);

      onClose();
    } catch (err) {
      console.error("Vendor payment tally failed:", err);
      setErrorMessage(err.message || "Failed to save vendor payment.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const dateFormat = defaultCompany?.dateFormat || "DD-MM-YYYY";

  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={onClose}
      title={isEditing ? `✏️ Edit Voucher ${editingPayment.paymentNo || ""}` : "💸 Pay Vendor — Settle Purchase Bills"}
      size="lg"
    >
      <form onSubmit={handleSavePayment} style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>

        {/* Helper Subtitle */}
        <div style={{ fontSize: "0.82rem", color: "var(--text-dim)", marginTop: "-0.5rem" }}>
          {isEditing
            ? "Change the amount, date, mode or how it is split. Bill balances and the vendor's payable are corrected automatically."
            : "Settle vendor dues across multiple purchase bills from oldest to newest (FIFO)."}
        </div>

        {/* 1. Vendor Card */}
        <div
          style={{
            background: "var(--bg-card)",
            border: "1px solid var(--border-subtle)",
            padding: "0.85rem 1rem",
            borderRadius: "var(--radius-md)"
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.4rem" }}>
            <span style={{ fontSize: "0.72rem", fontWeight: "700", color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.5px" }}>
              Vendor Details
            </span>
            {totalOpenBalance > 0 && (
              <Badge variant="danger" style={{ fontSize: "0.75rem", padding: "0.2rem 0.6rem" }}>
                Total Payable: ₹{totalOpenBalance.toLocaleString("en-IN")}
              </Badge>
            )}
          </div>

          {preselectedVendor || isEditing ? (
            <div>
              <div style={{ fontWeight: "700", fontSize: "1.05rem", color: "var(--text-main)" }}>
                {currentVendor?.name}
              </div>
              {currentVendor?.mobile && (
                <div style={{ fontSize: "0.8rem", color: "var(--text-dim)", marginTop: "2px" }}>
                  📞 {currentVendor.mobile}
                </div>
              )}
            </div>
          ) : (
            <div style={{ position: "relative" }}>
              <input
                type="text"
                className="form-control"
                placeholder="🔍 Search vendor by name or phone..."
                value={vendorSearch}
                onFocus={() => setIsVendorDropdownOpen(true)}
                onChange={(e) => { setVendorSearch(e.target.value); setIsVendorDropdownOpen(true); }}
                autoComplete="off"
              />
              {currentVendor && !isVendorDropdownOpen && (
                <div style={{ marginTop: "0.4rem", fontWeight: "700", color: "var(--text-main)" }}>
                  ✅ {currentVendor.name}
                  {currentVendor.mobile && <span style={{ fontWeight: "400", color: "var(--text-dim)", marginLeft: "0.4rem" }}>📞 {currentVendor.mobile}</span>}
                </div>
              )}
              {isVendorDropdownOpen && filteredVendorsForPicker.length > 0 && (
                <div style={{
                  position: "absolute", top: "100%", left: 0, right: 0, zIndex: 200,
                  background: "var(--bg-card)", border: "1px solid var(--border-subtle)",
                  borderRadius: "var(--radius-md)", maxHeight: "200px", overflowY: "auto",
                  boxShadow: "0 8px 24px rgba(0,0,0,0.25)", marginTop: "4px"
                }}>
                  {filteredVendorsForPicker.map((v) => (
                    <div
                      key={v.id}
                      onClick={() => {
                        setSelectedVendorId(v.id);
                        setVendorSearch("");
                        setIsVendorDropdownOpen(false);
                        setIsManualModified(false);
                      }}
                      style={{
                        padding: "0.6rem 0.85rem", cursor: "pointer",
                        borderBottom: "1px solid var(--border-subtle)",
                        background: v.id === selectedVendorId ? "var(--bg-hover)" : "transparent"
                      }}
                    >
                      <div style={{ fontWeight: "600", fontSize: "0.9rem" }}>{v.name}</div>
                      <div style={{ fontSize: "0.75rem", color: "var(--text-dim)" }}>
                        {v.mobile && `📞 ${v.mobile}`}
                        {dueOf(v) > 0 && ` • Payable: ₹${Number(dueOf(v)).toLocaleString("en-IN")}`}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {isVendorDropdownOpen && (
                <div
                  style={{ position: "fixed", inset: 0, zIndex: 199 }}
                  onClick={() => setIsVendorDropdownOpen(false)}
                />
              )}
            </div>
          )}
        </div>

        {/* 2. Payment Amount */}
        <div>
          <label style={{ display: "block", fontSize: "0.78rem", fontWeight: "700", color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "0.3rem" }}>
            Amount Paid (₹) *
          </label>
          <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
            <div className="currency-input-wrap" style={{ flex: 1 }}>
              <span className="currency-prefix">₹</span>
              <input
                type="number"
                step="0.01"
                min="1"
                placeholder="0.00"
                value={amountPaid}
                onChange={(e) => { setAmountPaid(e.target.value); setIsManualModified(false); }}
                className="form-control"
                style={{ paddingLeft: "2rem" }}
                required
              />
            </div>
            {totalOpenBalance > 0 && (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => { setAmountPaid(totalOpenBalance.toString()); setIsManualModified(false); }}
                style={{ whiteSpace: "nowrap", flexShrink: 0 }}
              >
                Pay Full Due
              </Button>
            )}
          </div>

          {/* Allocation Summary Chips */}
          {paidNum > 0 && (
            <div style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap", marginTop: "0.5rem" }}>
              <span style={{ fontSize: "0.76rem", padding: "2px 8px", borderRadius: "12px", background: "rgba(16,185,129,0.15)", color: "var(--success)", border: "1px solid rgba(16,185,129,0.3)", fontWeight: "600" }}>
                Applied: ₹{totalApplied.toLocaleString("en-IN")}
              </span>
              {unallocatedAmount > 0 && (
                <span style={{ fontSize: "0.76rem", padding: "2px 8px", borderRadius: "12px", background: "rgba(56,189,248,0.12)", color: "#38bdf8", border: "1px solid rgba(56,189,248,0.25)", fontWeight: "600" }}>
                  Advance: ₹{unallocatedAmount.toLocaleString("en-IN")}
                </span>
              )}
            </div>
          )}
        </div>

        {/* 3. Date, Mode, Reference */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
          <div>
            <label style={{ display: "block", fontSize: "0.78rem", fontWeight: "700", color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "0.3rem" }}>
              Payment Date
            </label>
            <input
              type="date"
              value={paymentDate}
              onChange={(e) => setPaymentDate(e.target.value)}
              className="form-control"
              required
            />
          </div>
          <div>
            <label style={{ display: "block", fontSize: "0.78rem", fontWeight: "700", color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "0.3rem" }}>
              Payment Mode
            </label>
            <select
              value={paymentMode}
              onChange={(e) => setPaymentMode(e.target.value)}
              className="form-control"
            >
              {PAYMENT_MODES.map((m) => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
          <div>
            <label style={{ display: "block", fontSize: "0.78rem", fontWeight: "700", color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "0.3rem" }}>
              Reference / UTR / Cheque #
            </label>
            <input
              type="text"
              placeholder="Optional"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              className="form-control"
            />
          </div>
          <div>
            <label style={{ display: "block", fontSize: "0.78rem", fontWeight: "700", color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "0.3rem" }}>
              Notes
            </label>
            <input
              type="text"
              placeholder="Optional"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="form-control"
            />
          </div>
        </div>

        {/* 4. Open Bills Allocation Table */}
        {openVendorBills.length > 0 && paidNum > 0 && (
          <div>
            <div style={{ fontSize: "0.78rem", fontWeight: "700", color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "0.5rem" }}>
              📋 Bill Settlement Allocation
            </div>
            <div style={{ border: "1px solid var(--border-subtle)", borderRadius: "var(--radius-md)", overflow: "hidden" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.78rem" }}>
                <thead>
                  <tr style={{ background: "var(--bg-card)", borderBottom: "1px solid var(--border-subtle)" }}>
                    <th style={{ padding: "6px 8px", textAlign: "left", color: "var(--text-muted)", fontWeight: "700" }}>Bill #</th>
                    <th style={{ padding: "6px 8px", textAlign: "left", color: "var(--text-muted)", fontWeight: "700" }}>Date</th>
                    <th style={{ padding: "6px 8px", textAlign: "right", color: "var(--text-muted)", fontWeight: "700" }}>Due</th>
                    <th style={{ padding: "6px 8px", textAlign: "right", color: "var(--text-muted)", fontWeight: "700", minWidth: "90px" }}>Pay Now</th>
                    <th style={{ padding: "6px 8px", textAlign: "center", color: "var(--text-muted)", fontWeight: "700" }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {allocations.map((alloc) => {
                    const statusVariant = alloc.nextStatus === "Paid" ? "success" : alloc.nextStatus === "Partial" ? "warning" : "danger";
                    return (
                      <tr key={alloc.invoiceId} style={{ borderBottom: "1px solid var(--border-subtle)" }}>
                        <td style={{ padding: "6px 8px", fontWeight: "600", color: "var(--text-main)" }}>
                          {alloc.invoiceNo}
                        </td>
                        <td style={{ padding: "6px 8px", color: "var(--text-dim)" }}>
                          {formatAppDate(alloc.invoiceDate, dateFormat)}
                        </td>
                        <td style={{ padding: "6px 8px", textAlign: "right", color: "var(--text-main)" }}>
                          ₹{alloc.balanceDue.toLocaleString("en-IN")}
                        </td>
                        <td style={{ padding: "6px 4px", textAlign: "right" }}>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            max={alloc.balanceDue}
                            value={alloc.allocatedAmount}
                            onChange={(e) => handleManualAllocationChange(alloc.invoiceId, e.target.value)}
                            className="form-control"
                            style={{ width: "85px", fontSize: "0.78rem", padding: "4px 6px", textAlign: "right" }}
                          />
                        </td>
                        <td style={{ padding: "6px 8px", textAlign: "center" }}>
                          <Badge variant={statusVariant} style={{ fontSize: "0.68rem", padding: "2px 6px" }}>
                            {alloc.nextStatus}
                          </Badge>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {openVendorBills.length === 0 && selectedVendorId && (
          <div style={{ background: "var(--bg-card)", border: "1px solid var(--border-subtle)", padding: "1rem", borderRadius: "var(--radius-md)", textAlign: "center", color: "var(--text-dim)", fontSize: "0.85rem" }}>
            ✅ No open purchase bills for this vendor. Payment will be recorded as advance.
          </div>
        )}

        {/* Error */}
        {errorMessage && (
          <div style={{ background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.3)", borderRadius: "var(--radius-md)", padding: "0.7rem 1rem", color: "var(--danger)", fontSize: "0.82rem" }}>
            ⚠️ {errorMessage}
          </div>
        )}

        {/* Submit */}
        <div style={{ display: "flex", gap: "0.5rem", paddingTop: "0.25rem" }}>
          <Button
            type="submit"
            variant="primary"
            disabled={submitting || paidNum <= 0}
            style={{ flex: 1, minHeight: "44px" }}
          >
            {submitting
              ? "Saving Payment…"
              : `${isEditing ? "💾 Save Changes" : "💸 Save Vendor Payment"} ₹${paidNum > 0 ? paidNum.toLocaleString("en-IN") : "0"}`}
          </Button>
          <Button type="button" variant="secondary" onClick={onClose} style={{ minHeight: "44px" }}>
            Cancel
          </Button>
        </div>

      </form>
    </BottomSheet>
  );
}

export default VendorPaymentModal;
